import React from 'react';
import { useHistory, Redirect, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Button,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  InputAdornment,
  Chip,
  Badge,
  Popover,
  Typography,
} from '@material-ui/core';
import {
  Menu as MenuIcon,
  AccountCircle,
  ArrowBack,
  Language as LanguageIcon,
  Notifications,
} from '@material-ui/icons';

import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import { getLanguages } from 'services/languageServices';
import Language from 'types/models/Language';
import UserRoleType from 'types/enums/UserRoleType';
import AuthData from 'types/models/AuthData';
import StorageType from 'types/enums/StorageType';
import PermissionCode from 'types/enums/PermissionCode';
import useSetupInitialState from 'hooks/useSetupInitialState';
import usePermissions from 'hooks/usePermissions';
import usePendingAcceptance from 'hooks/usePendingAcceptance';
import { hasPermission } from 'utils/hasPermission';
import 'styles/components/AppHeader.scss';
import '../translation/config';

const getRoleName = (authData: AuthData | null): string => {
  if (!authData?.user.role || !authData) return '';
  if (authData.user.role === UserRoleType.VenueEditor) {
    return 'roles.venue-editor';
  } else if (authData.user.role === UserRoleType.CommerceEditor) {
    return 'roles.commerce-editor';
  } else if (authData.user.role === UserRoleType.FranchiseEditor) {
    return 'roles.franchise-editor';
  } else if (authData.user.role === UserRoleType.WarehouseEditor) {
    return 'roles.warehouse-editor';
  }

  return 'roles.admin';
};

export default (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { authData, setAuthData, setCurrentPageListNumber, permissions } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const { setupPermissions } = usePermissions();
  const [toggleDrawer, setToggleDrawer] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [languages, setLanguages] = React.useState<Language[]>();
  const [bellAnchorEl, setBellAnchorEl] = React.useState<null | HTMLElement>(null);
  const { visible: pendingVisible, count: pendingCount } = usePendingAcceptance(authData, entity);

  const open = Boolean(anchorEl);
  const role = authData?.user?.role;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    setAuthData(null);
    const ls = [StorageType.AuthData, StorageType.CurrentCommerce, StorageType.CurrentVenue];
    ls.forEach((item) => localStorage.removeItem(item));
    handleClose();
    return <Redirect to="/public/login" />;
  };

  const goToPage = (route: string) => {
    // Si estamos en un formulario, reemplazamos en vez de pushear para que el
    // formulario no quede como destino del back (al volver se salta el form).
    if (isFormRoute(location.pathname)) {
      history.replace(route);
    } else {
      history.push(route);
    }
    setToggleDrawer(false);
  };

  const loadLanguages = React.useCallback(async () => {
    const languages = await getLanguages();
    if (languages) setLanguages(languages);
  }, []);

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const isFormRoute = (pathname: string): boolean => /\/(create|edit)(\/|$)/.test(pathname);

  const getSafeBackTarget = (pathname: string): string => {
    if (pathname === '/private/stock/home') return '/private/welcome';
    if (pathname.startsWith('/private/stock')) return '/private/stock/home';
    return '/private/welcome';
  };

  // Al montar, cantidad de entradas del browser. Si no creció, no hay historial in-app detrás (link profundo directo) -> usamos ruta segura.
  const initialHistoryLength = React.useRef(window.history.length);

  const handleBack = () => {
    if (window.history.length <= initialHistoryLength.current) {
      history.push(getSafeBackTarget(location.pathname));
    } else {
      history.go(-1);
    }
    setCurrentPageListNumber(1);
  };

  const showBackButton = !location.pathname.includes('private/welcome');

  React.useEffect(() => {
    loadLanguages();
  }, []);

  React.useEffect(() => {
    if (!authData) return;
    setupPermissions();
  }, [authData]);

  const LanguageSelector = (): JSX.Element => {
    return (
      <Select
        className="language-selector"
        displayEmpty
        startAdornment={
          <InputAdornment position="start">
            <LanguageIcon />
          </InputAdornment>
        }
        style={{ color: '#FFF' }}
      >
        <MenuItem>{t('app-header-language')}</MenuItem>
        {languages?.map((language) => (
          <MenuItem
            onClick={() => {
              handleChangeLanguage(language.code);
            }}
            value={language.id}
            key={language.id}
          >
            {language.description}
          </MenuItem>
        ))}
      </Select>
    );
  };

  return (
    <AppBar position="static">
      <Toolbar style={{ maxHeight: 56 }}>
        <>
          {authData && (
            <>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => {
                  setToggleDrawer(true);
                }}
              >
                <MenuIcon />
              </IconButton>
              {showBackButton && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => {
                    handleBack();
                  }}
                >
                  <ArrowBack />
                </IconButton>
              )}
              {entity && <p className="entity-name">{entity.name}</p>}
              <header className="app-header">
                <div className="app-header-content">
                  <LanguageSelector />

                  <span className="separator" />

                  {pendingVisible && pendingCount > 0 && (
                    <IconButton
                      color="inherit"
                      className="p-0"
                      aria-label={t('stock.pending.bell.view')}
                      onClick={(e: React.MouseEvent<HTMLElement>) => setBellAnchorEl(e.currentTarget)}
                    >
                      <Badge badgeContent={pendingCount} color="secondary" overlap="circle">
                        <Notifications />
                      </Badge>
                    </IconButton>
                  )}

                  <div className="user-info">
                    <div className="user-text">
                      {authData.user.refundAuthorizationCode && (
                        <Chip label={authData.user.refundAuthorizationCode} size="small" className="auth-code-chip" />
                      )}
                      <div className="user-name-col">
                        <Tooltip title={<span style={{ fontSize: 14 }}>{authData.user.name}</span>}>
                          <p className="user-name">{authData.user.name || '-'}</p>
                        </Tooltip>
                        <p className="user-role">{t(getRoleName(authData))}</p>
                      </div>
                    </div>
                    <IconButton onClick={handleMenu} color="inherit" className="p-0">
                      <AccountCircle style={{ fontSize: 32 }} />
                    </IconButton>
                  </div>
                </div>

                <Menu id="menu-appbar" anchorEl={anchorEl} open={open} onClose={handleClose}>
                  <MenuItem onClick={handleLogOut}>{t('app-header-logout')}</MenuItem>
                </Menu>

                <Popover
                  open={Boolean(bellAnchorEl)}
                  anchorEl={bellAnchorEl}
                  onClose={() => setBellAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <div style={{ padding: 16, maxWidth: 280 }}>
                    <Typography variant="body2">{t('stock.pending.bell.message', { count: pendingCount })}</Typography>
                    <Button
                      color="primary"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        setBellAnchorEl(null);
                        history.push('/private/pending-movements');
                      }}
                    >
                      {t('stock.pending.bell.view')}
                    </Button>
                  </div>
                </Popover>
              </header>
            </>
          )}
        </>
      </Toolbar>
      <Drawer
        anchor="left"
        open={toggleDrawer}
        onClose={() => {
          setToggleDrawer(false);
        }}
      >
        <Button
          onClick={() => {
            goToPage('/');
          }}
        >
          {t('app-header-home')}
        </Button>
        {(authData?.user?.role === UserRoleType.Admin || authData?.user.role === UserRoleType.VenueEditor) && (
          <>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/venues');
              }}
            >
              {t('app-header-venues')}
            </Button>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/benefits');
              }}
            >
              {t('app-header-benefits')}
            </Button>
          </>
        )}
        {(authData?.user?.role === UserRoleType.Admin || authData?.user?.role === UserRoleType.FranchiseEditor) && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/franchises');
            }}
          >
            {t('app-header-franchise')}
          </Button>
        )}
        {authData?.user?.role !== UserRoleType.WarehouseEditor && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/commerces');
            }}
          >
            {t('app-header-commerces')}
          </Button>
        )}
        {hasPermission(permissions, PermissionCode.UsersView) && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/users');
            }}
          >
            {t('app-header-users')}
          </Button>
        )}
        {hasPermission(permissions, PermissionCode.CommerceCategoriesView) && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/commerce-categories');
            }}
          >
            {t('app-header-commerces-categories')}
          </Button>
        )}
        {hasPermission(permissions, PermissionCode.ProductCategoriesView) && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/product-categories');
            }}
          >
            {t('app-header-products')}
          </Button>
        )}
        {hasPermission(permissions, PermissionCode.PaymentMethodsView) && (
          <Button
            onClick={() => {
              goToPage('/private/admin/payments');
            }}
          >
            {t('app-payment-methods')}
          </Button>
        )}
        {authData?.user?.role === UserRoleType.Admin && (
          <>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/admin/twitter-configs');
              }}
            >
              {t('app-header-twitter')}
            </Button>
          </>
        )}
        {(role === UserRoleType.Admin || role === UserRoleType.VenueEditor) && (
          <Button
            onClick={() => {
              goToPage('/private/protocol-orders');
            }}
          >
            {t('app-header-orders')}
          </Button>
        )}

        {authData?.user?.role !== UserRoleType.WarehouseEditor && (
          <Button
            onClick={() => {
              goToPage('/private/reporting');
            }}
          >
            {t('app-header-reports')}
          </Button>
        )}
        {role === UserRoleType.Admin && (
          <Button
            onClick={() => {
              goToPage('/private/admin/system-config');
            }}
          >
            {t('app-header-system-config')}
          </Button>
        )}
        {[UserRoleType.Admin].includes(role as UserRoleType) && (
          <Button
            onClick={() => {
              goToPage('/private/admin/permissions');
            }}
          >
            {t('app-header-permissions')}
          </Button>
        )}

        {(role === UserRoleType.Admin ||
          (role === UserRoleType.VenueEditor && entity && 'usesStockModule' in entity && entity.usesStockModule) ||
          role === UserRoleType.WarehouseEditor) && (
          <Button
            onClick={() => {
              goToPage('/private/stock/home');
            }}
          >
            {t('app-header-stock')}
          </Button>
        )}
      </Drawer>
    </AppBar>
  );
};
